import type { SetType } from '@legalzoom/types';
import { useNavigation } from 'react-router';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { LoadingIndicatorType } from './LoadingIndicator';

export type Position =
  | 'fullscreen'
  | 'navExpanded'
  | 'navCollapsed'
  | undefined;

type LoadingContextType = {
  isLoading: boolean | string;
  navigationIndicator: LoadingIndicatorType;
  setLoading: (_isLoading: boolean | string) => void;
  setNavigationIndicator: (_type: LoadingIndicatorType) => void;
  isDisplayed: boolean;
  position: Position;
  setPosition: (_position: Position) => void;
};

export const LoadingContext = createContext<LoadingContextType>({
  isLoading: false as boolean | string,
  navigationIndicator: 'spinner' as LoadingIndicatorType,
  setLoading(_isLoading: boolean | string) {},
  setNavigationIndicator(_type: LoadingIndicatorType) {},
  isDisplayed: false,
  position: undefined,
  setPosition(_position: Position) {},
});

export const loadingStates = new Set(['loading'] as const);
export type LoadingState = SetType<typeof loadingStates>;

const isDisplayed = true;
export function LoadingProvider({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const [loadingState, setLoadingState] = useState<boolean | string>(false);
  const [navigationIndicator, setNavigationIndicator] =
    useState<LoadingIndicatorType>('spinner');
  const navigation = useNavigation();
  const isLoading =
    loadingState || loadingStates.has(navigation.state as LoadingState);
  const [position, setPosition] = useState<Position>();

  useEffect(() => {
    if (navigation.state === 'idle' && isLoading) {
      // if navigation changes to idle but loading has been manually set, unset it
      // this prevents locking the user in a loading state after a SSR redirect
      setLoadingState(false);
    }
  }, [navigation.state]);

  useEffect(() => {
    if (!isLoading) {
      // the indicator will 'automatically' reset to default after each use
      setNavigationIndicator('spinner');
    }
  }, [isLoading]);

  const value = useMemo(
    () => ({
      isLoading,
      navigationIndicator,
      setLoading: setLoadingState,
      setNavigationIndicator,
      isDisplayed,
      position,
      setPosition,
    }),
    [
      isLoading,
      navigationIndicator,
      setLoadingState,
      setNavigationIndicator,
      position,
      setPosition,
    ],
  );
  return (
    <LoadingContext.Provider value={value}>{children}</LoadingContext.Provider>
  );
}

export const useLoading = () => useContext(LoadingContext);
